import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, TrendingUp, TrendingDown, AlertTriangle, Lightbulb } from 'lucide-react';
import type { PropertyUnit, CategoryDistribution, RevenueMetrics, UnitInterestMetrics } from '@/lib/admin/analytics';

interface RecommendationsPanelProps {
  units: PropertyUnit[];
  categoryData: CategoryDistribution[];
  revenue: RevenueMetrics;
  unitInterest: UnitInterestMetrics[];
}

interface Recommendation {
  type: 'pricing' | 'inventory' | 'sales' | 'insight';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  action?: string;
  units?: string[];
}

export function RecommendationsPanel({
  units,
  categoryData,
  revenue,
  unitInterest,
}: RecommendationsPanelProps) {
  const recommendations: Recommendation[] = [];

  // Pricing optimization recommendations
  categoryData.forEach((category) => {
    const categoryUnits = units.filter((u) => u.category === category.category && u.price && u.price > 0);
    if (categoryUnits.length === 0) return;

    const prices = categoryUnits.map((u) => u.price!);
    const avgPrice = category.averagePrice;
    const highPricedUnits = categoryUnits.filter(
      (u) => u.price! > avgPrice * 1.15 && u.availability === 'available'
    );
    const lowPricedUnits = categoryUnits.filter(
      (u) => u.price! < avgPrice * 0.85 && u.availability === 'available'
    );

    if (highPricedUnits.length > 0) {
      recommendations.push({
        type: 'pricing',
        priority: 'medium',
        title: `High-priced ${category.category} units`,
        description: `${highPricedUnits.length} ${category.category} unit(s) priced significantly above category average (${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(avgPrice)})`,
        action: 'Consider reviewing pricing strategy or highlighting premium features',
        units: highPricedUnits.map((u) => u.unit_number),
      });
    }

    if (lowPricedUnits.length > 0) {
      recommendations.push({
        type: 'pricing',
        priority: 'high',
        title: `Opportunity: Under-priced ${category.category} units`,
        description: `${lowPricedUnits.length} ${category.category} unit(s) priced below market average. These may represent good value opportunities.`,
        action: 'Consider promoting these units or reviewing if pricing is accurate',
        units: lowPricedUnits.map((u) => u.unit_number),
      });
    }
  });

  // Inventory management recommendations
  categoryData.forEach((category) => {
    const availabilityRate = category.total > 0 ? (category.available / category.total) * 100 : 0;
    if (availabilityRate < 20 && category.total > 5) {
      recommendations.push({
        type: 'inventory',
        priority: 'high',
        title: `Low availability in ${category.category}`,
        description: `Only ${category.available} of ${category.total} ${category.category} units available (${availabilityRate.toFixed(1)}%). This category is in high demand.`,
        action: 'Consider prioritizing sales efforts or reviewing reserved units',
      });
    }

    if (availabilityRate > 80 && category.total > 5) {
      recommendations.push({
        type: 'inventory',
        priority: 'medium',
        title: `High availability in ${category.category}`,
        description: `${category.available} of ${category.total} ${category.category} units available (${availabilityRate.toFixed(1)}%). May need marketing focus.`,
        action: 'Consider targeted marketing campaigns or promotional pricing',
      });
    }
  });

  // Sales strategy recommendations
  const highInterestAvailable = unitInterest
    .filter((interest) => {
      const unit = units.find((u) => u.id === interest.unitId);
      return unit && unit.availability === 'available' && interest.engagementScore > 50;
    })
    .slice(0, 5);

  if (highInterestAvailable.length > 0) {
    recommendations.push({
      type: 'sales',
      priority: 'high',
      title: 'High-interest units still available',
      description: `${highInterestAvailable.length} unit(s) with high engagement scores are still available. These represent strong conversion opportunities.`,
      action: 'Prioritize follow-up with interested leads for these units',
      units: highInterestAvailable.map((i) => i.unitNumber),
    });
  }

  // Market insights
  const fastestSellingCategory = categoryData.reduce((prev, current) => {
    const prevSoldRate = prev.total > 0 ? (prev.sold / prev.total) * 100 : 0;
    const currentSoldRate = current.total > 0 ? (current.sold / current.total) * 100 : 0;
    return currentSoldRate > prevSoldRate ? current : prev;
  });

  if (fastestSellingCategory.sold > 0) {
    recommendations.push({
      type: 'insight',
      priority: 'low',
      title: `Fastest selling category: ${fastestSellingCategory.category}`,
      description: `${fastestSellingCategory.sold} of ${fastestSellingCategory.total} units sold (${((fastestSellingCategory.sold / fastestSellingCategory.total) * 100).toFixed(1)}%). This category shows strong market demand.`,
      action: 'Consider highlighting this category in marketing materials',
    });
  }

  const getIcon = (type: Recommendation['type']) => {
    switch (type) {
      case 'pricing':
        return <TrendingUp className="h-4 w-4" />;
      case 'inventory':
        return <AlertTriangle className="h-4 w-4" />;
      case 'sales':
        return <Lightbulb className="h-4 w-4" />;
      case 'insight':
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: Recommendation['priority']) => {
    switch (priority) {
      case 'high':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'medium':
        return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'low':
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const sortedRecommendations = recommendations.sort((a, b) => {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Business Intelligence Recommendations</CardTitle>
        <CardDescription>Actionable insights and recommendations</CardDescription>
      </CardHeader>
      <CardContent>
        {sortedRecommendations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No specific recommendations at this time.</p>
            <p className="text-sm mt-2">All metrics are within expected ranges.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedRecommendations.map((rec, index) => (
              <div
                key={index}
                className={`p-4 border rounded-lg ${getPriorityColor(rec.priority)}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{getIcon(rec.type)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{rec.title}</h4>
                      <Badge variant="outline" className="text-xs">
                        {rec.priority}
                      </Badge>
                    </div>
                    <p className="text-sm mb-2">{rec.description}</p>
                    {rec.action && (
                      <p className="text-sm font-medium mt-2">{rec.action}</p>
                    )}
                    {rec.units && rec.units.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {rec.units.slice(0, 5).map((unitNum) => (
                          <Badge key={unitNum} variant="secondary" className="text-xs">
                            {unitNum}
                          </Badge>
                        ))}
                        {rec.units.length > 5 && (
                          <Badge variant="secondary" className="text-xs">
                            +{rec.units.length - 5} more
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}







